import type { ReactNode } from 'react';
import React, { memo } from 'react';
import { css, useTheme } from '@emotion/react';
import { Drawer } from '@mantine/core';
import type { CallbackFunction, LinkEntryType } from 'tsconfig/types';
import { CmsLink, HeaderLogo, Icon } from 'common-ui';
import { useDisclosure } from '@mantine/hooks';
import { useScreenLayout } from '../../hooks/useScreenLayout';

export type DrawerMenuProps = {
  target: ReactNode;
  children: ReactNode;
  position?: 'left' | 'right';
  onClose?: CallbackFunction;
  className?: string;
  isAccessibleFor?: string;
  stickyContent?: LinkEntryType[];
  keepMounted?: boolean;
};

export const DrawerMenu = memo((props: DrawerMenuProps) => {
  const {
    target,
    children,
    className,
    position = 'right',
    onClose,
    isAccessibleFor,
    stickyContent,
    keepMounted = true,
  } = props;
  const theme = useTheme();
  const [opened, { close, toggle }] = useDisclosure(false);
  const { isMobile } = useScreenLayout();

  const mobileFocusStyles = css`
    ${theme.breakpoints.lg.replace('@media', '@media not all and')} {
      && .mantine-Drawer-body a[href]:focus-visible,
      && .mantine-Drawer-body button:focus-visible,
      && .mantine-Drawer-body [role='button'][tabindex]:focus-visible,
      && .mantine-Drawer-body .drawer-header-logo-section #header-logo a:focus-visible {
        outline: 2px solid ${theme.colors.blue600};
        outline-offset: -2px;
        box-shadow: inset 0 0 0 4px ${theme.colors.white};
      }
    }
  `;

  const mobileDrawerContainerStyles = css`
    .mantine-Drawer-overlay,
      .mantine-Drawer-inner {
        ${theme.breakpoints.lg} {
          display: none;
        }
      } 
    }
  `;

  function doClose() {
    close();
    if (onClose) {
      onClose();
    }
  }

  function handleControlKeyDown(event: React.KeyboardEvent<HTMLDivElement>, action: () => void) {
    if (isMobile) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }
    action();
  }

  return (
    <div>
      <div onClick={toggle} onKeyDown={event => handleControlKeyDown(event, toggle)} role="button" tabIndex={0}>
        {target}
      </div>
      <Drawer
        opened={opened}
        onClose={doClose}
        withCloseButton={false}
        css={[
          mobileFocusStyles,
          theme.widgets.DrawerMenu?.drawerContainer,
          isAccessibleFor === 'notDesktop' && mobileDrawerContainerStyles,
        ]}
        position={position}
        className={className}
        keepMounted={keepMounted}
      >
        <div className="drawer-header-logo-section">
          <HeaderLogo isDrawer={true} />
          <div
            onClick={doClose}
            onKeyDown={event => handleControlKeyDown(event, doClose)}
            role="button"
            tabIndex={0}
            data-testid="close-button"
          >
            <Icon kind="close" size="xs" />
          </div>
        </div>
        <div data-testid="drawer-main" className="drawer-main">
          {children}
        </div>
        {stickyContent && (
          <div className="drawer-footer-sticky-button-section">
            {stickyContent.map(link => (
              <div key={`sticky-button-${link.fields?.title}`}>
                <CmsLink contentItem={link} />
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
});
